import React, { useEffect, useState } from 'react';
import { expenseAPI } from '../api/client';
import Layout from '../components/Layout';

function TaxReport() {
  const [expenses, setExpenses] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [fullscreenReceipt, setFullscreenReceipt] = useState(null);

  useEffect(() => {
    loadTaxData();
  }, []);

  const loadTaxData = async () => {
    setLoading(true);
    try {
      // Load all expenses
      const expenseRes = await expenseAPI.getAll({ limit: 1000 });
      setExpenses(expenseRes.data.data || expenseRes.data || []);
    } catch (err) {
      console.error('Failed to load tax report data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByDate = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 1000,
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
      };

      const expenseRes = await expenseAPI.getAll(params);
      setExpenses(expenseRes.data.data || expenseRes.data || []);
    } catch (err) {
      console.error('Failed to filter expenses', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTaxStats = () => {
    const filteredExpenses = expenses.filter(exp => {
      if (dateRange.startDate && new Date(exp.date) < new Date(dateRange.startDate)) return false;
      if (dateRange.endDate && new Date(exp.date) > new Date(dateRange.endDate)) return false;
      return true;
    });

    const totalTax = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.tax_amount || 0), 0);
    const deductibleExpenses = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    const totalWithTax = filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount || 0) + parseFloat(exp.tax_amount || 0)), 0);

    return {
      totalTax,
      deductibleExpenses,
      totalWithTax,
      expenseCount: filteredExpenses.length,
    };
  };

  const downloadReport = () => {
    const stats = calculateTaxStats();
    const csv = [
      ['Tax Report', ''],
      ['Generated on', new Date().toLocaleDateString()],
      ['Date Range', `${dateRange.startDate || 'All'} to ${dateRange.endDate || 'All'}`],
      [''],
      ['Tax Summary'],
      ['Total Tax Amount', `$${stats.totalTax.toFixed(2)}`],
      ['Deductible Expenses', `$${stats.deductibleExpenses.toFixed(2)}`],
      ['Total (Expenses + Tax)', `$${stats.totalWithTax.toFixed(2)}`],
      ['Number of Expenses', stats.expenseCount],
      [''],
      ['Expense Details'],
      ['Date', 'Category', 'Amount', 'Tax', 'Total', 'Payment Mode', 'Description'],
      ...expenses
        .filter(exp => {
          if (dateRange.startDate && new Date(exp.date) < new Date(dateRange.startDate)) return false;
          if (dateRange.endDate && new Date(exp.date) > new Date(dateRange.endDate)) return false;
          return true;
        })
        .map(exp => [
          new Date(exp.date).toLocaleDateString(),
          exp.category,
          `$${parseFloat(exp.amount || 0).toFixed(2)}`,
          `$${parseFloat(exp.tax_amount || 0).toFixed(2)}`,
          `$${(parseFloat(exp.amount || 0) + parseFloat(exp.tax_amount || 0)).toFixed(2)}`,
          exp.payment_mode,
          exp.description,
        ]),
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const stats = calculateTaxStats();

  const ExpenseModal = () => {
    if (!selectedExpense) return null;

    const amount = parseFloat(selectedExpense.amount || 0);
    const taxAmount = parseFloat(selectedExpense.tax_amount || 0);
    const total = amount + taxAmount;
    const status = (selectedExpense.status || 'pending').toLowerCase();
    const isImageReceipt = selectedExpense.receipt && /\.(jpg|jpeg|png|gif|webp)$/i.test(selectedExpense.receipt);

    return (
      <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200">
          <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-900 text-white px-6 py-5 flex justify-between items-center border-b border-white/10 z-10">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-blue-200/80">Expense Preview</p>
              <h2 className="text-xl font-bold mt-1">{selectedExpense.category || 'Expense Details'}</h2>
            </div>
            <button
              onClick={() => {
                setShowModal(false);
                setSelectedExpense(null);
              }}
              className="text-white/90 hover:bg-white/15 rounded-xl p-2 transition"
            >
              ✕
            </button>
          </div>

          <div className="p-6 md:p-7 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">${amount.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Tax</p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">${taxAmount.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Total</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">${total.toFixed(2)}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <p className="text-sm font-semibold text-slate-800">Expense Snapshot</p>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Date</p>
                    <p className="font-semibold text-slate-900">{new Date(selectedExpense.date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Payment Mode</p>
                    <p className="font-semibold text-slate-900">{selectedExpense.payment_mode || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Vendor</p>
                    <p className="font-semibold text-slate-900">{selectedExpense.vendor || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Reference Number</p>
                    <p className="font-semibold text-slate-900">{selectedExpense.reference_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Created Date</p>
                    <p className="font-semibold text-slate-900">{selectedExpense.createdAt ? new Date(selectedExpense.createdAt).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Status</p>
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      status === 'approved'
                        ? 'bg-emerald-100 text-emerald-700'
                        : status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-rose-100 text-rose-700'
                    }`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedExpense.description && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Description</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selectedExpense.description}</p>
                </div>
              )}
            </div>

            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Receipt</p>
                {!selectedExpense.receipt && (
                  <p className="text-sm text-slate-500">No receipt attached.</p>
                )}

                {selectedExpense.receipt && isImageReceipt && (
                  <>
                    <img
                      src={selectedExpense.receipt}
                      alt="Receipt"
                      className="w-full h-44 object-cover rounded-xl border border-slate-200 mb-3 cursor-pointer hover:opacity-95 transition"
                      onClick={() => setFullscreenReceipt(selectedExpense.receipt)}
                    />
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        onClick={() => setFullscreenReceipt(selectedExpense.receipt)}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-semibold transition"
                      >
                        View Fullscreen
                      </button>
                      <a
                        href={selectedExpense.receipt}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-semibold transition"
                      >
                        Download Receipt
                      </a>
                    </div>
                  </>
                )}

                {selectedExpense.receipt && !isImageReceipt && (
                  <a
                    href={selectedExpense.receipt}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-4 py-2 rounded-xl bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-semibold transition"
                  >
                    View Receipt File
                  </a>
                )}
              </div>

              {selectedExpense.pdf && (
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Attached PDF</p>
                  <p className="text-sm text-slate-700 truncate mb-3">{selectedExpense.pdf.split('/').pop()}</p>
                  <a
                    href={selectedExpense.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center justify-center w-full px-4 py-2 rounded-xl bg-rose-100 text-rose-700 hover:bg-rose-200 text-sm font-semibold transition"
                  >
                    View PDF
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Close Button */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
            <button
              onClick={() => {
                setShowModal(false);
                setSelectedExpense(null);
              }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 transition font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fullscreen Receipt Viewer
  const FullscreenReceiptViewer = () => {
    if (!fullscreenReceipt) return null;

    return (
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="relative bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-screen overflow-auto border border-slate-200">
          <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-900 text-white px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Receipt Preview</h2>
            <button
              onClick={() => setFullscreenReceipt(null)}
              className="text-white hover:bg-white/15 rounded-xl p-2 transition text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="p-6 md:p-8 bg-slate-100/60 flex items-center justify-center min-h-[70vh]">
            <img
              src={fullscreenReceipt} 
              alt="Receipt Fullscreen"
              className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-xl"
            />
          </div>
          <div className="sticky bottom-0 bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-between items-center gap-3">
            <p className="text-sm text-slate-600">{fullscreenReceipt.split('/').pop()}</p>
            <div className="flex gap-3">
              <a
                href={fullscreenReceipt}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition font-semibold"
              >
                Download Receipt
              </a>
              <button
                onClick={() => setFullscreenReceipt(null)}
                className="px-4 py-2 bg-slate-300 text-slate-700 rounded-xl hover:bg-slate-400 transition font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout pageTitle="Tax Report" pageSubtitle="Generate and view your tax report">
      <div>
        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Filter by Date Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleFilterByDate}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 transition disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Filter'}
              </button>
              <button
                onClick={downloadReport}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition disabled:opacity-50"
              >
                📥 Download CSV
              </button>
            </div>
          </div>
        </div>

        {/* Tax Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <p className="text-sm font-medium opacity-90">Total Tax Amount</p>
            <p className="text-3xl font-bold mt-2">${stats.totalTax.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <p className="text-sm font-medium opacity-90">Deductible Expenses</p>
            <p className="text-3xl font-bold mt-2">${stats.deductibleExpenses.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <p className="text-sm font-medium opacity-90">Total (Expenses + Tax)</p>
            <p className="text-3xl font-bold mt-2">${stats.totalWithTax.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <p className="text-sm font-medium opacity-90">Expense Count</p>
            <p className="text-3xl font-bold mt-2">{stats.expenseCount}</p>
          </div>
        </div>

        {/* Tax Details Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Expense Details</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tax</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Total</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Payment Mode</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {expenses
                  .filter(exp => {
                    if (dateRange.startDate && new Date(exp.date) < new Date(dateRange.startDate)) return false;
                    if (dateRange.endDate && new Date(exp.date) > new Date(dateRange.endDate)) return false;
                    return true;
                  })
                  .map((expense) => (
                    <tr 
                      key={expense.id} 
                      onClick={() => {
                        setSelectedExpense(expense);
                        setShowModal(true);
                      }}
                      className="hover:bg-orange-50 transition cursor-pointer border-l-4 border-transparent hover:border-orange-500"
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">{new Date(expense.date).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{expense.category}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">${parseFloat(expense.amount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-orange-600 font-semibold">${parseFloat(expense.tax_amount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">${(parseFloat(expense.amount || 0) + parseFloat(expense.tax_amount || 0)).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{expense.payment_mode || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{expense.description}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {expenses.filter(exp => {
          if (dateRange.startDate && new Date(exp.date) < new Date(dateRange.startDate)) return false;
          if (dateRange.endDate && new Date(exp.date) > new Date(dateRange.endDate)) return false;
          return true;
        }).length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4 text-center text-yellow-800">
            No expenses found for the selected date range.
          </div>
        )}

        {showModal && <ExpenseModal />}
        <FullscreenReceiptViewer />
      </div>
    </Layout>
  );
}

export default TaxReport;
